import {
  Dialog,
  Transition,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { X } from "lucide-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { toast } from "sonner";
import {
  useGetAllProvidersQuery,
  useAddVendorServiceMutation,
  useUpdateServiceMutation,
  useGetSingleServiceQuery,
} from "../../../../redux/service/vendorService";
import AddServiceForm from "./AddServiceForm";
import ATMLoadingButton from "../../../Atom/ATMLoadingButton/ATMLoadingButton";

const AddServiceFormWrapper = ({ isOpen, onClose, editingService }) => {
  const [providers, setProviders] = useState([]);
  const { data, isLoading, isFetching } = useGetAllProvidersQuery();
  const {
    data: serviceData,
    isLoading: serviceDataIsLoading,
    isFetching: serviceDataIsFetching,
  } = useGetSingleServiceQuery(editingService?.id, {
    skip: !editingService?.id,
  });
  const [updateService, updateServiceInfo] = useUpdateServiceMutation();
  const [addVendorService, addVendorServiceInfo] =
    useAddVendorServiceMutation();

  const [singleService, setSingleService] = useState({});

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setProviders(data?.data);
    }
  }, [data, isLoading, isFetching]);

  useEffect(() => {
    if (!serviceDataIsFetching && !serviceDataIsLoading) {
      setSingleService(serviceData);
    }
  }, [serviceData, serviceDataIsLoading, serviceDataIsFetching]);

  const ServiceSchemaValidation = Yup.object().shape({
    name: Yup.string().required("Service name is required"),
    description: Yup.string().required("Description is required"),
    category: Yup.string().required("Category is required"),
    duration: Yup.number().min(1).required("Duration is required"),
    price: Yup.number().min(0).required("Price is required"),
    bufferTime: Yup.number().min(0),
  });

  const initialValues = {
    name: singleService?.name || "",
    description: singleService?.description || "",
    category: singleService?.category || "",
    duration: singleService?.duration || 60,
    price: singleService?.price || 0,
    bufferTime: singleService?.buffer_time || 0,
    colorCode: singleService?.color_code || "#22c55e",
    providers: singleService?.providers
      ? singleService?.providers?.map((el) => el?.id)
      : [],
    image: singleService?.image || [],
  };

  const handleAddVendorService = async (values, { resetForm }) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("description", values.description);
    formData.append("category", values.category);
    formData.append("duration", values.duration);
    formData.append("price", values.price);
    formData.append("buffer_time", values.bufferTime);
    formData.append("color_code", values.colorCode);

    values.providers.forEach((id, index) => {
      formData.append(`providers`, Number(id));
    });
    values.image.forEach((image) => {
      formData.append("images", image);
    });
    try {
      await addVendorService(formData).unwrap();
      toast.success("Service added successfully");
      resetForm();
      onClose();
    } catch {
      toast.error("Failed to add Service.");
    }
  };

  const handleUpdateVendorService = async (values, { resetForm }) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("description", values.description);
    formData.append("category", values.category);
    formData.append("duration", values.duration);
    formData.append("price", values.price);
    formData.append("buffer_time", values.bufferTime);
    formData.append("color_code", values.colorCode);

    values.providers.forEach((id, index) => {
      formData.append(`providers`, Number(id));
    });
    values.image.forEach((image) => {
      if (typeof image !== "string") {
        formData.append("images", image);
      } else {
        formData.append("existing_images", image);
      }
    });
    try {
      await updateService({ body: formData, id: editingService?.id }).unwrap();
      toast.success("Service updated successfully");
      resetForm();
      onClose();
      setSingleService({});
    } catch {
      toast.error("Failed to update Service.");
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center px-4 py-8 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold text-gray-900"
                    >
                      {editingService ? "Edit Service" : "Add New Service"}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 mt-1">
                      Fill in the service details below
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      onClose();
                      setSingleService({});
                    }}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>

                <Formik
                  initialValues={initialValues}
                  validationSchema={ServiceSchemaValidation}
                  enableReinitialize
                  onSubmit={
                    editingService
                      ? handleUpdateVendorService
                      : handleAddVendorService
                  }
                >
                  {(formikProps) => (
                    <Form>
                      <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
                        {serviceDataIsFetching || serviceDataIsLoading ? (
                          <div className="flex justify-center items-center h-[70vh]">
                            <svg
                              className="animate-spin h-12 w-22 text-orange-500"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          <AddServiceForm
                            formikProps={formikProps}
                            providers={providers}
                          />
                        )}
                      </div>

                      <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            setSingleService({});
                          }}
                          className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <ATMLoadingButton
                          label={
                            editingService ? "Update Service" : "Add Service"
                          }
                          type="submit"
                          loading={
                            addVendorServiceInfo?.isLoading ||
                            updateServiceInfo?.isLoading
                          }
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddServiceFormWrapper;
